import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { Close, ContentCut, ErrorOutline, Movie, Replay, VolumeOff } from '@mui/icons-material';
import TrimmedVideo from 'components/play/recovery/TrimmedVideo';

const EASE = 'cubic-bezier(0.32,0.72,0,1)';

/**
 * Milisegundos a m:ss. Se TRUNCA (no se redondea) para que el rótulo coincida con el reloj
 * del reproductor, que también trunca: si el corte va en 18.9 s, el video muestra 0:18 y el
 * chip diría 0:19, y la entrenadora creería que el recorte quedó movido.
 */
const formatMs = (ms) => {
  const totalSeconds = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

const stageSx = {
  position: 'relative',
  width: '100%',
  aspectRatio: '16 / 9',
  minHeight: 180,
  borderRadius: '14px',
  overflow: 'hidden',
  backgroundColor: 'surface.raised',
  border: '1px solid',
  borderColor: 'hairline.main'
};

const overlaySx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 1,
  px: 3
};

// Chip informativo (no accionable): dorado como acento pequeño sobre la superficie de siempre.
const trimChipSx = {
  height: 26,
  borderRadius: 999,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: 'gold.main',
  backgroundColor: 'surface.overlay',
  border: '1px solid',
  borderColor: 'hairline.main',
  '& .MuiChip-icon': { fontSize: 14, color: 'gold.main' }
};

const retryButtonSx = {
  mt: 0.5,
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 2.5,
  px: 2,
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'hairline.main',
  transition: `color 200ms ${EASE}, border-color 200ms ${EASE}, transform 200ms ${EASE}`,
  '&:hover': { color: 'gold.main', borderColor: 'hairline.strong' },
  '&:active': { transform: 'scale(0.98)' }
};

/**
 * OTRIX Play (admin) — vista previa del video de una práctica o de un paso. Resuelve dos cosas
 * que hoy no tienen respuesta: comprobar que el archivo que se subió es el correcto, y ver el
 * recorte (`startMs`/`endMs`) tal cual lo verá el cliente, sin volver a subir nada.
 *
 * Dos decisiones deliberadas:
 * 1. `objectFit: 'contain'` — en una vista previa hay que ver el encuadre REAL; un `cover`
 *    recortaría los bordes y la entrenadora aprobaría un video que en la app se ve distinto.
 * 2. El video solo se monta con el modal abierto (`keepMounted` apagado, que es el defecto del
 *    Dialog): un `<video>` oculto sigue descargando bytes del plan de datos del móvil.
 */
function VideoPreviewModal({ open, src = null, title = null, startMs = null, endMs = null, hasAudio = false, onClose }) {
  const { t } = useTranslation(['play']);
  const [failed, setFailed] = useState(false);
  const [blocked, setBlocked] = useState(false);
  // Cambiar esta cuenta remonta el <TrimmedVideo>: un fallo de red se reintenta sin cerrar el modal.
  const [attempt, setAttempt] = useState(0);

  // Cada apertura (y cada cambio de archivo) parte de cero: si no, el error de un video anterior
  // se queda pegado y el siguiente nunca llega a pintarse.
  useEffect(() => {
    setFailed(false);
    setBlocked(false);
    setAttempt(0);
  }, [open, src]);

  const hasStart = typeof startMs === 'number' && startMs > 0;
  const hasEnd = typeof endMs === 'number' && endMs > 0;
  const isTrimmed = hasStart || hasEnd;

  const trimLabel = () => {
    if (hasStart && hasEnd) {
      return t('recoveryAdmin.preview.trimRange', {
        ns: 'play',
        start: formatMs(startMs),
        end: formatMs(endMs),
        defaultValue: 'Fragmento {{start}} a {{end}}'
      });
    }
    if (hasStart) {
      return t('recoveryAdmin.preview.trimFrom', {
        ns: 'play',
        start: formatMs(startMs),
        defaultValue: 'Fragmento desde {{start}}'
      });
    }
    return t('recoveryAdmin.preview.trimTo', {
      ns: 'play',
      end: formatMs(endMs),
      defaultValue: 'Fragmento hasta {{end}}'
    });
  };

  const renderStage = () => {
    // El error se resuelve DENTRO del modal: un rectángulo negro mudo no le dice a nadie que
    // el archivo no cargó, y un toast se va antes de que la entrenadora lo relacione con esto.
    if (failed) {
      return (
        <Box sx={overlaySx}>
          <ErrorOutline sx={{ fontSize: 32, color: 'sessionStatus.absent.fg' }} />
          <Typography variant="subtitle2" fontWeight={800}>
            {t('recoveryAdmin.preview.errorTitle', { ns: 'play', defaultValue: 'No pudimos cargar el video' })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 360, lineHeight: 1.5 }}>
            {t('recoveryAdmin.preview.errorBody', {
              ns: 'play',
              defaultValue: 'Revisa tu conexión y vuelve a intentar. Si sigue igual, sube el archivo de nuevo.'
            })}
          </Typography>
          <Button
            size="small"
            startIcon={<Replay sx={{ fontSize: 16 }} />}
            onClick={() => {
              setFailed(false);
              setBlocked(false);
              setAttempt((current) => current + 1);
            }}
            sx={retryButtonSx}>
            {t('recoveryAdmin.preview.retry', { ns: 'play', defaultValue: 'Reintentar' })}
          </Button>
        </Box>
      );
    }

    if (!src) {
      return (
        <Box sx={overlaySx}>
          <Movie sx={{ fontSize: 32, color: 'text.disabled' }} />
          <Typography variant="subtitle2" fontWeight={800}>
            {t('recoveryAdmin.preview.emptyTitle', { ns: 'play', defaultValue: 'Todavía no hay video' })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 360, lineHeight: 1.5 }}>
            {t('recoveryAdmin.preview.emptyBody', {
              ns: 'play',
              defaultValue: 'Graba o sube un video y aparecerá aquí para revisarlo.'
            })}
          </Typography>
        </Box>
      );
    }

    return (
      <TrimmedVideo
        key={attempt}
        src={src}
        startMs={startMs}
        endMs={endMs}
        muted={!hasAudio}
        loop={false}
        autoPlay
        controls
        objectFit="contain"
        onError={() => setFailed(true)}
        onPlayBlocked={() => setBlocked(true)}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="otx-video-preview-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          // Sin esto MUI pinta un gradiente encima del papel y ensucia el off-black.
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Movie sx={{ color: 'gold.main', flex: 'none' }} />
          <Typography id="otx-video-preview-title" variant="h6" fontWeight={800} letterSpacing="-0.02em" noWrap>
            {title || t('recoveryAdmin.preview.title', { ns: 'play', defaultValue: 'Vista previa del video' })}
          </Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ flex: 'none', color: 'text.secondary' }}
          aria-label={t('recoveryAdmin.preview.close', { ns: 'play', defaultValue: 'Cerrar vista previa' })}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
        <Stack spacing={1.5}>
          <Box sx={stageSx}>{open ? renderStage() : null}</Box>

          {(isTrimmed || !hasAudio) && !failed && src && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {isTrimmed && <Chip size="small" icon={<ContentCut />} label={trimLabel()} sx={trimChipSx} />}
              {!hasAudio && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <VolumeOff sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {t('recoveryAdmin.preview.noAudio', { ns: 'play', defaultValue: 'Este video se reproduce sin audio.' })}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}

          {isTrimmed && !failed && src && (
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {t('recoveryAdmin.preview.trimHint', {
                ns: 'play',
                defaultValue: 'Estás viendo solo el fragmento recortado, igual que lo verá el cliente.'
              })}
            </Typography>
          )}

          {/* Safari y Chrome móvil rechazan el autoplay con sonido: sin este aviso la pantalla
              parece congelada aunque el video esté perfecto. */}
          {blocked && !failed && src && (
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {t('recoveryAdmin.preview.blocked', {
                ns: 'play',
                defaultValue: 'Toca reproducir para ver el video.'
              })}
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

VideoPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Esta versión de `require-default-props` todavía exige `defaultProps`, que React 18.3 ya
  // deprecó en componentes de función: el valor por defecto va en la firma.
  // `src` es opcional a propósito: el padre suele montar el modal cerrado con la fila todavía
  // sin elegir (`preview?.url`), y marcarlo requerido llenaría la consola de avisos.
  // eslint-disable-next-line react/require-default-props
  src: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  startMs: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  endMs: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  hasAudio: PropTypes.bool
};

export default VideoPreviewModal;
